import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  carTitle,
  formatCarPrice,
  formatCarTrim,
  formatOdometer,
  type CarListItem,
} from '../carInventoryTypes';

interface Props {
  car: CarListItem;
  index: number;
}

export const CarCard: React.FC<Props> = ({ car, index }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const title = carTitle(car);
  const trimLabel = formatCarTrim(car.trim);
  const showImage = car.cover_photo_url && !imageFailed;

  return (
    <motion.div
      className={`car-card${car.is_sold ? ' car-card--sold' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.05, ease: [0.4, 0, 0.2, 1] }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
    >
      <Link
        to={`/vehicles/${car.id}`}
        className="car-card-link"
        aria-label={car.is_sold ? `View sold listing ${title}` : `View ${title}`}
      >
        <div className="car-card-thumb">
          {showImage ? (
            <img
              src={car.cover_photo_url!}
              alt={title}
              className="car-card-img"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div className="car-card-img car-card-img--empty">
              <i className="ti ti-car" aria-hidden />
              <span>Photos coming soon</span>
            </div>
          )}
          {car.is_sold && <span className="car-sold-badge">Sold</span>}
          {!car.is_sold && car.is_special_price && (
            <span className="car-special-badge">Special price</span>
          )}
          {car.photo_count > 1 && (
            <span className="car-card-photo-count">
              <i className="ti ti-photo" aria-hidden /> {car.photo_count}
            </span>
          )}
        </div>

        <div className="car-card-body">
          <h3 className="car-card-title">{title}</h3>
          {trimLabel && <p className="car-card-trim">{trimLabel}</p>}
          <div className="car-card-meta">
            <span>
              <i className="ti ti-gauge" aria-hidden /> {formatOdometer(car.odometer_km)}
            </span>
            {car.stock_number && <span>Stock #{car.stock_number}</span>}
          </div>
        </div>

        <div className="car-card-foot">
          <span className="car-card-price">
            {car.is_sold ? (
              <>
                <span className="car-card-price-was">{formatCarPrice(car.price)}</span>
                <span className="car-card-price-sold">Sold</span>
              </>
            ) : car.is_special_price && car.list_price && car.list_price > (car.price ?? 0) ? (
              <>
                <span className="car-card-price-was">{formatCarPrice(car.list_price)}</span>
                {formatCarPrice(car.price)}
              </>
            ) : (
              formatCarPrice(car.price)
            )}
          </span>
          <span className="car-card-cta">
            {car.is_sold ? 'View listing' : 'View details'} <i className="ti ti-arrow-right" aria-hidden />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export const CarCardSkeleton: React.FC = () => (
  <div className="car-card car-card--skeleton" aria-hidden>
    <div className="car-card-thumb" />
    <div className="car-card-body">
      <div className="car-skeleton-line car-skeleton-line--short" />
      <div className="car-skeleton-line" />
      <div className="car-skeleton-line car-skeleton-line--mid" />
    </div>
  </div>
);
